// Pooled parallel dialer — shared views for the member panel, the admin insights
// tab, and the pool status endpoints. All values are live-session state (v1 pools
// are in-memory per backend process; counters reset with the session).

// "idle": the wrap-up countdown expired without the agent pressing Next — they
// are settled and parked out of the queue until they explicitly re-join or leave.
export type PooledDialerMemberState = "connecting" | "free" | "busy" | "wrapup" | "manual" | "idle";

// How long a seat waits for its extension to re-REGISTER before the member is
// removed ("line_wait_timeout"). The fleet re-registers within seconds of a
// socket drop; the long tail is a binding FreeSWITCH expired without telling
// the client, which only the client's own REGISTER repairs — it re-REGISTERs as
// soon as it sees the seat waiting, and that round trip needs room here.
// Shared because the waiting panel counts against this bound: a private copy on
// either side would let the UI promise a seat the server had already released.
export const POOL_LINE_WAIT_TIMEOUT_MS = 90_000;

// Mid-call rescue: how long a prospect whose agent's tab died is held for the
// agent's new tab before the call is ended. Shared because the reloaded tab
// retries its claim inside the same window.
export const POOL_CALL_RESCUE_WINDOW_MS = 30_000;

export interface PooledDialerMemberView {
  userId: string;
  // An outbound AI agent's seat is deliberately indistinguishable here: the floor must see an
  // ordinary member.
  username: string;
  state: PooledDialerMemberState;
  callsHandled: number;
  // The live contact for busy members, or the callback/recovery contact for a
  // member temporarily working in the normal manual workspace. The name is
  // business-first (resolved at claim time) — it's what the agent opens with.
  currentContactId: string | null;
  currentContactName: string | null;
  currentContactPhone: string | null;
  // The contact leg a busy member is bridged to — the id Agent Assist events
  // (suggestions/checklist SSE) are keyed by, so the member's own live stage
  // can render them. Null outside a bridged outbound pool call (inbound
  // handoffs and manual work never arm assist).
  currentLegId: string | null;
  // True while the member is on a handed-off inbound return call.
  inbound: boolean;
  // A "connecting" member whose seat is waiting for their phone line to
  // re-register with the switch; the server originates the line itself when
  // it does. The client renders the wait rather than a generic connecting.
  linePending: boolean;
  // When the seat's line wait started, and so what the server's 90s hold is
  // measured against. Its own clock, not stateChangedAt: a seat that was
  // already "connecting" when the line dropped never changed state, and a
  // flapping registration keeps the first wait's clock rather than restarting
  // the bound. Null when the seat is not (and was not) waiting for a line.
  lineWaitStartedAt: string | null;
  manualCampaignId: string | null;
  // When the member last became bridgeable (free members) / was bridged (busy).
  stateSince: string;
  // When the engine last moved this member between states — the same stamp
  // carried by technical_pool_state_events, so a server-side join can
  // attribute a client report to its transition. Distinct from stateSince,
  // which reports the leg's answer time for busy members because that is the
  // elapsed clock the floor wants to read.
  stateChangedAt: string;
  // Queue priority (card 439): higher skips the wait line. 0 = normal; the
  // floor only badges non-zero values.
  poolPriority: number;
  // Bridge cutoff (card 440): null = default. Non-null badges the member as
  // strict on the floor.
  bridgeCutoff: number | null;
  // Server-computed waiting-stage preview: the answered leg the engine's own
  // dry-run assignment says is most likely headed to this member (null =
  // none). The client renders this instead of guessing.
  upNextLegId: string | null;
  // 1-based position in the engine's own queue ordering (priority, idle, and
  // skip-mode yields — which the client cannot reconstruct). Null when the
  // member is not currently in the free queue.
  queuePosition: number | null;
  // Wrap-up time-box: when the server will park this member idle (ISO).
  // Null outside wrap-up — and null inside it when a booked meeting suspended
  // the countdown. While frozen (see wrapupFrozen) this reports the deadline
  // shifted by the frozen time, so a ticking client renders a paused timer.
  wrapupDeadline: string | null;
  // The countdown is held while the member has the booking modal open. Floor
  // view only — the agent's own stage deliberately does not announce it.
  wrapupFrozen: boolean;
  // The member's mid-call rescue switch (users.pool_call_rescue): the owner
  // tab remembers a live call across a reload only when it is on.
  callRescue: boolean;
  // While the member's agent leg is being replaced mid-call (a tab reload):
  // when the held prospect is hung up if the new tab has not taken the call
  // back. Null otherwise.
  rescueDeadline: string | null;
}

export interface PooledDialerMembershipResponse {
  membership: {
    campaignId: string;
    member: PooledDialerMemberView;
  } | null;
  // Server clock when this response was built, so an agent reached by the poll
  // rather than the stream can still measure publication lag without a browser
  // clock in the subtraction.
  serverAt: string;
  // True while this process is shutting down for a deploy: memberships are
  // being released server-side and the next process takes over shortly. A
  // client seeing this must not read its emptied seat as a phone fault.
  restarting: boolean;
  // Set when the caller holds no seat but the previous process recorded them
  // as pooled when it shut down (within the handoff window). The client arms
  // its own rejoin from it; the server never originates a line on its behalf.
  restartHandoff: { campaignId: string; leftAt: string } | null;
}

export type PooledDialerEventKind =
  | "agent_joined"
  | "agent_left"
  | "lines_launched"
  | "answered"
  | "bridged"
  | "abandoned"
  | "amd_killed"
  | "ai_passed_over"
  | "hot_transferred"
  | "rescued"
  | "rescue_lost"
  | "queue_empty";

export interface PooledDialerEventView {
  id: number;
  at: string;
  kind: PooledDialerEventKind;
  agentUsername: string | null;
  contactName: string | null;
  detail: string | null;
  // Contact-leg id the event refers to (null for agent/pace events) — lets the
  // live view attribute a chip's exit to its reason.
  legId: string | null;
}

export type PooledDialerLegState = "ringing" | "screening" | "holding" | "bridged";

export interface PooledDialerLegView {
  legId: string;
  contactId: string;
  state: PooledDialerLegState;
  // Masked number while ringing ("+1 480 ····87"); the business name once
  // answered (screening/holding/bridged).
  label: string;
  // When the leg entered its current state.
  stateSince: string;
  // Talking legs: which agent has it.
  agentUsername: string | null;
}

export interface PooledDialerPacingView {
  dialLevel: number;
  targetLegs: number;
  // Session abandon % (abandoned / answered) as the brake evaluates it.
  abandonPct: number;
  brakeActive: boolean;
  brakeEnabled: boolean;
}

export interface PooledDialerLegCounts {
  ringing: number;
  holding: number;
  bridged: number;
}

export interface PooledDialerCounters {
  originated: number;
  answered: number;
  bridged: number;
  abandoned: number;
  amdKills: number;
  noAnswer: number;
  didStarved: number;
  hotTransferred: number;
}

export interface PooledDialerPoolView {
  sessionId: string;
  campaignId: string;
  // Enriched at the HTTP layer (the engine only knows ids).
  campaignName: string | null;
  // Practice sandbox (campaigns.is_practice): Agent Assist runs for every
  // member here, whatever their per-user flag.
  isPractice: boolean;
  // Server clock when this view was built. Paired with a member's
  // stateChangedAt it gives a client a pure server-clock measure of how long a
  // transition took to be published, with no browser clock in the subtraction.
  serverAt: string;
  startedAt: string;
  answerRateEwma: number;
  members: PooledDialerMemberView[];
  legCounts: PooledDialerLegCounts;
  counters: PooledDialerCounters;
  // Head of the priority-ordered free queue (priority first, longest idle
  // among equals) — who receives the next answered call, before per-leg
  // eligibility (bridge cutoffs) is applied.
  nextUserId: string | null;
  pacing: PooledDialerPacingView | null;
  events: PooledDialerEventView[];
  legs: PooledDialerLegView[];
}

export interface PooledDialerStatusResponse {
  pool: PooledDialerPoolView | null;
  settings: {
    enabled: boolean;
  };
  // The viewer's own callbacks that have come due. One of these claims a free
  // member within seconds of joining and moves them to the manual workspace,
  // so the join screen says so first — unannounced, the handoff reads as a
  // failure to join the pool at all.
  dueCallbacks: number;
}

export interface PooledDialerInsightsResponse {
  pools: PooledDialerPoolView[];
}
